// Métabolisme et dépense énergétique.
import type { ActivityLevel, Sex } from './types';

// BMR — Mifflin-St Jeor (1990). s = +5 (homme) / −161 (femme).
export function bmrMifflinStJeor(
  weightKg: number,
  heightCm: number,
  age: number,
  sex: Sex,
): number {
  const s = sex === 'male' ? 5 : -161;
  return 10 * weightKg + 6.25 * heightCm - 5 * age + s;
}

// Facteurs d'activité (PAL) appliqués au BMR pour obtenir le TDEE.
export const ACTIVITY_FACTORS: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
};

export const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  sedentary: 'Sédentaire',
  light: 'Légèrement actif',
  moderate: 'Modérément actif',
  active: 'Très actif',
  very_active: 'Extrêmement actif',
};

// Exemples concrets par palier PAL (descriptions usuelles des facteurs Mifflin-St Jeor),
// pour lever l'ambiguïté du choix de niveau d'activité.
export const ACTIVITY_DESCRIPTIONS: Record<ActivityLevel, string> = {
  sedentary: 'Travail de bureau, peu ou pas d’exercice',
  light: 'Exercice léger 1 à 3 j/sem',
  moderate: 'Exercice modéré 3 à 5 j/sem',
  active: 'Exercice intense 6 à 7 j/sem',
  very_active: 'Exercice très intense ou métier physique',
};

// TDEE (maintenance) = BMR × facteur d'activité.
export function tdee(bmr: number, activity: ActivityLevel): number {
  return bmr * ACTIVITY_FACTORS[activity];
}

// Coefficients d'Atwater (kcal/g).
export const ATWATER = {
  protein: 4,
  carbs: 4,
  fat: 9,
  alcohol: 7,
} as const;

// Énergie (kcal) reconstituée depuis les macros via Atwater.
export function energyFromMacros(macros: {
  protein: number;
  carbs: number;
  fat: number;
  alcohol?: number;
}): number {
  return (
    macros.protein * ATWATER.protein +
    macros.carbs * ATWATER.carbs +
    macros.fat * ATWATER.fat +
    (macros.alcohol ?? 0) * ATWATER.alcohol
  );
}
